/**
 * Page Outline — セクションリスト + DnD 並び替え + ステータスバッジ。
 */
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SECTIONS, type SectionType } from '@goenhub/landing-sections';
import type { LandingSectionRef } from '@goenhub/shared';
import { deleteDoc, doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import {
  Bookmark,
  Copy,
  Eye,
  EyeOff,
  GripVertical,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';

import { AddSectionDialog } from './AddSectionDialog';
import { LandingRegenerateDialog } from './LandingRegenerateDialog';
import { LandingSaveTemplateDialog } from './LandingSaveTemplateDialog';
import { SectionStatusPill } from './SectionStatusPill';

import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import {
  useRegenerateLandingSection,
  useUpdateLanding,
  type LandingRecord,
} from '@/hooks/useLandings';
import { db } from '@/lib/firebase';

function typeLabel(type: string): string {
  return SECTIONS[type as SectionType]?.label ?? type;
}
function variantLabel(type: string, variant: string): string {
  return SECTIONS[type as SectionType]?.variantLabels?.[variant] ?? variant;
}

interface Props {
  landingId: string;
  landing: LandingRecord;
  activeSectionId: string | null;
  onSelect: (id: string) => void;
  onEdit: (id: string) => void;
}

export function PageOutline({ landingId, landing, activeSectionId, onSelect, onEdit }: Props) {
  const sections = (landing.sections ?? []).slice().sort((a, b) => a.order - b.order);
  const totalWords = sections.reduce((s, sec) => s + (sec.wordCount ?? 0), 0);
  const [addOpen, setAddOpen] = useState(false);
  const [tplSection, setTplSection] = useState<LandingSectionRef | null>(null);
  const [regenSec, setRegenSec] = useState<LandingSectionRef | null>(null);

  const updateLanding = useUpdateLanding();
  const regenSection = useRegenerateLandingSection();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = sections.findIndex((s) => s.id === active.id);
    const newIdx = sections.findIndex((s) => s.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const reordered = arrayMove(sections, oldIdx, newIdx).map((s, i) => ({
      ...s,
      order: i,
    }));
    void updateLanding.mutateAsync({ id: landingId, data: { sections: reordered } });
  };

  const handleToggleHidden = async (id: string) => {
    const next = sections.map((s) => (s.id === id ? { ...s, hidden: !s.hidden } : s));
    try {
      await updateLanding.mutateAsync({ id: landingId, data: { sections: next } });
    } catch (e) {
      toast.error(`更新失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const handleDuplicate = async (id: string) => {
    const idx = sections.findIndex((s) => s.id === id);
    if (idx < 0) return;
    const source = sections[idx];
    const newId = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    try {
      // 元セクションのサブコレクション content を読み込んで複製
      const sourceSnap = await getDoc(doc(db, 'landings', landingId, 'sections', source.id));
      const sourceData = sourceSnap.exists()
        ? (sourceSnap.data() as Record<string, unknown>)
        : null;
      await setDoc(doc(db, 'landings', landingId, 'sections', newId), {
        type: source.type,
        variant: source.variant,
        order: source.order + 0.5, // ソート用の暫定値、直後 reorder で正される
        content: sourceData?.content ?? {},
        imageRefs: sourceData?.imageRefs ?? {},
        updatedAt: serverTimestamp(),
      });
      const newRef: LandingSectionRef = {
        ...source,
        id: newId,
        order: source.order + 0.5,
      };
      // 元セクションの直下に挿入し、order を 0..n に振り直す
      const next = [...sections.slice(0, idx + 1), newRef, ...sections.slice(idx + 1)].map(
        (s, i) => ({ ...s, order: i }),
      );
      await updateLanding.mutateAsync({ id: landingId, data: { sections: next } });
      toast.success('セクションを複製しました');
    } catch (e) {
      toast.error(`複製失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  return (
    <div className="p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold">ページ構成</div>
          <div className="text-[11px] text-[var(--text-muted)]">
            {sections.length} セクション · {totalWords} 単語
          </div>
        </div>
      </div>

      {sections.length === 0 && (
        <div className="text-xs text-[var(--text-muted)] p-3 border border-dashed border-[var(--border)] rounded-md text-center">
          生成後にセクションリストが表示されます。
        </div>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {sections.map((s, idx) => (
              <SectionCard
                key={s.id}
                index={idx + 1}
                section={s}
                statusPill={
                  <SectionStatusPill landingId={landingId} sections={sections} section={s} />
                }
                isActive={s.id === activeSectionId}
                onSelect={() => onSelect(s.id)}
                onEdit={() => onEdit(s.id)}
                onToggleHidden={() => handleToggleHidden(s.id)}
                onDuplicate={() => handleDuplicate(s.id)}
                onSaveTemplate={() => setTplSection(s)}
                onRegenerate={() => setRegenSec(s)}
                onDelete={async () => {
                  if (!confirm(`「${typeLabel(s.type)}」セクションを削除しますか？`)) return;
                  try {
                    await deleteDoc(doc(db, 'landings', landingId, 'sections', s.id));
                    const nextSections = sections
                      .filter((x) => x.id !== s.id)
                      .map((x, i) => ({ ...x, order: i }));
                    await updateLanding.mutateAsync({
                      id: landingId,
                      data: { sections: nextSections },
                    });
                    toast.success('セクションを削除しました');
                  } catch (e) {
                    toast.error(`削除失敗: ${e instanceof Error ? e.message : String(e)}`);
                  }
                }}
                regenerating={regenSection.isPending && regenSec?.id === s.id}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <button
        type="button"
        className="w-full text-xs text-[var(--text-muted)] border border-dashed border-[var(--border)] rounded-md py-2 hover:bg-[var(--bg-hover)]"
        onClick={() => setAddOpen(true)}
      >
        <Plus className="size-3 inline mr-1" /> セクションを追加
      </button>

      <AddSectionDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        landingId={landingId}
        landing={landing}
      />

      <LandingSaveTemplateDialog
        open={!!tplSection}
        onOpenChange={(o) => {
          if (!o) setTplSection(null);
        }}
        landingId={landingId}
        section={tplSection}
        sectionLabel={tplSection ? typeLabel(tplSection.type) : undefined}
      />

      <LandingRegenerateDialog
        open={!!regenSec}
        onOpenChange={(o) => {
          if (!o) setRegenSec(null);
        }}
        landingId={landingId}
        sectionId={regenSec?.id ?? null}
        sectionLabel={regenSec ? typeLabel(regenSec.type) : undefined}
      />
    </div>
  );
}

interface CardProps {
  index: number;
  section: LandingSectionRef;
  statusPill: React.ReactNode;
  isActive: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onRegenerate: () => void;
  onDelete: () => void;
  onToggleHidden: () => void;
  onDuplicate: () => void;
  onSaveTemplate: () => void;
  regenerating: boolean;
}

function SectionCard({
  index,
  section,
  statusPill,
  isActive,
  onSelect,
  onEdit,
  onRegenerate,
  onDelete,
  onToggleHidden,
  onDuplicate,
  onSaveTemplate,
  regenerating,
}: CardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
  });
  const baseOpacity = section.hidden ? 0.45 : 1;
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : baseOpacity,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`p-2 rounded-md border transition-colors cursor-pointer ${
        isActive
          ? 'border-[var(--accent)] bg-[var(--accent-subtle)]'
          : 'border-[var(--border)] bg-[var(--bg-elevated)] hover:bg-[var(--bg-hover)]'
      }`}
      onClick={onSelect}
    >
      <div className="flex items-start gap-2">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab text-[var(--text-muted)] mt-1"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="size-4" />
        </button>
        <div className="text-[11px] text-[var(--text-muted)] font-mono w-6">
          {String(index).padStart(2, '0')}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 mb-0.5">
            <span className="text-[10px] font-semibold text-[var(--text-muted)]">
              {typeLabel(section.type)}
            </span>
            <span className="text-[10px] text-[var(--text-muted)]">·</span>
            <span className="text-[10px] text-[var(--text-muted)]">
              {variantLabel(section.type, section.variant)}
            </span>
          </div>
          <div className="text-xs font-medium truncate">{section.title || '(無題)'}</div>
        </div>
        {statusPill}
      </div>
      {isActive && (
        <div className="mt-2 flex items-center gap-1.5">
          <Button
            size="sm"
            variant="secondary"
            onClick={(e) => {
              e.stopPropagation();
              onRegenerate();
            }}
            disabled={regenerating}
          >
            {regenerating ? (
              <Loader2 className="size-3 animate-spin" />
            ) : (
              <RefreshCw className="size-3" />
            )}
            <span className="ml-1">再生成</span>
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
          >
            <Pencil className="size-3" />
            <span className="ml-1">編集</span>
          </Button>
          <div className="ml-auto flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate();
              }}
              className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              title="このセクションを複製"
            >
              <Copy className="size-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onToggleHidden();
              }}
              className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              title={section.hidden ? '公開ページに表示する' : '公開ページから非表示にする'}
            >
              {section.hidden ? <EyeOff className="size-3" /> : <Eye className="size-3" />}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onSaveTemplate();
              }}
              className="text-[var(--text-muted)] hover:text-[var(--accent)]"
              title="このセクションをテンプレートとして保存"
            >
              <Bookmark className="size-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="text-[var(--text-muted)] hover:text-red-500"
              title="このセクションを削除"
            >
              <Trash2 className="size-3" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
